import React, { memo, useMemo, useState } from "react"
import { toast } from "react-toastify"
import {
  useCompanyConnectorControllerGetCompanyConnectorsQuery,
  useCompanyConnectorControllerGetAirbyteSetupErrorQuery,
  useCompanyConnectorControllerUpdateCompanyConnectorMutation,
} from "~/ui-rtk/api/companyConnectorApi"
import { renderStatusIcon } from "../../utils"
import SetupErrorBanner from "../../../SetupErrorBanner/SetupErrorBanner"
import ConnectorStatus from "../ConnectorStatus"
import AccountInfo from "../AccountInfo"
import { Button } from "~/ui-rtk/components/ui/controls"
import { useConnect as useConnectorStatusConnect } from "../ConnectorStatus/connect"
import {
  mapConnectorStatusWithVersionCheck,
  RESETTABLE_STATUSES,
} from "~/ui-rtk/utils/connector-utils"
import { isConnectorVersionUpToDate } from "~/ui-rtk/utils/connector-version-utils"
import useConnectors from "~/ui-rtk/hooks/connectors"
import { ConnectionStatus, type CompanyConnectorDto } from "~/ui-rtk/api/types"
import { useConnectorsTracking } from "~/ui-rtk/hooks/useConnectorsTracking"
import type { ConnectorStatus as ConnectorStatusType } from "~/ui-rtk/shared/types/connectors"
import { getButtonText } from "../../../ConnectorsTable/components/SourceItem/SourceItem"
import ResetSetupConfirmDialog from "../../../ResetSetupConfirmDialog"
import { CredentialGrantWarningChip } from "../../../CredentialGrantWarning"

type ConnectionListProps = {
  companyConnectorId: string
  isSuperAdmin: boolean
  allowMultipleConnections?: boolean
  onDisconnect: (id: string) => Promise<void>
  onReconnect: (companyConnectorId?: string) => void
  onClose: () => void
  onAddConnection?: () => void
}

const ConnectionItemDetails: React.FC<{
  connector: CompanyConnectorDto
  index: number
  isSuperAdmin: boolean
  onDisconnect: (id: string) => Promise<void>
  onReconnect: (companyConnectorId?: string) => void
  onClose: () => void
}> = ({
  connector,
  index,
  isSuperAdmin,
  onDisconnect,
  onReconnect,
  onClose,
}) => {
  const { connectors } = useConnectors()
  const { connectorInfo, isLoading: isLoadingStatus } =
    useConnectorStatusConnect(connector.id)
  const [updateCompanyConnector, { isLoading: isUpdating }] =
    useCompanyConnectorControllerUpdateCompanyConnectorMutation()
  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false)
  const {
    trackReconnectClicked,
    trackDisconnectClicked,
    trackRemoveMigrationRestrictions,
  } = useConnectorsTracking()

  const connectorDefinition = connectors?.find(
    c => c.id === connector.serviceName,
  )
  const isVersionUpToDate = connectorDefinition
    ? isConnectorVersionUpToDate(connectorDefinition, connector)
    : true

  const hasError =
    connector.status === ConnectionStatus.PENDING_RECONNECT ||
    connector.status === ConnectionStatus.PENDING_SETUP
  const { data: errorData } =
    useCompanyConnectorControllerGetAirbyteSetupErrorQuery(
      { id: connector.id },
      { skip: !hasError },
    )

  const isCreatedByMigration = connector.metadata?.createdByMigration === true

  const connectorStatus = useMemo(() => {
    // Live poller health takes precedence: an unhealthy or pending-setup connection
    // needs user action (reconnect / finish setup) regardless of scope version drift.
    if (connectorInfo?.status === "unhealthy") return "RECONNECT"
    if (connectorInfo?.status === "pending_setup") return "PENDING_SETUP"
    // Healthy (or unknown) poller state — delegate to the shared mapping, which only
    // bumps to UPGRADE when the DB status is CONNECTED and the version is behind.
    return mapConnectorStatusWithVersionCheck(
      connector.status,
      isSuperAdmin,
      isVersionUpToDate,
    )
  }, [connectorInfo?.status, connector.status, isSuperAdmin, isVersionUpToDate])

  const isHealthy = connectorInfo?.status === "healthy"

  // A healthy connection whose Meta grant no longer covers its account (see
  // CredentialGrantWarningChip) is only usable again after a reconnect, so
  // the healthy footer offers one; unhealthy connections already have it.
  const grantWarning = connector.metadata?.credentialGrantWarning ?? null
  const grantNeedsReconnect = !!grantWarning && isHealthy

  const handleReconnect = () => {
    if (isCreatedByMigration) {
      toast.error(
        "This connector was created by the system and cannot be re-authenticated. Please contact your administrator if you need to modify this connection.",
        { toastId: "migration-connector-reconnect-dialog-blocked" },
      )
      return
    }
    trackReconnectClicked({
      company_connector_id: connector.id,
      service_name: connector.serviceName,
      connector_status: connectorStatus,
      is_healthy: isHealthy,
      is_super_admin: isSuperAdmin,
      connection_index: index,
    })
    void onReconnect(connector.id)
    void onClose()
  }

  const handleDisconnect = async () => {
    trackDisconnectClicked({
      company_connector_id: connector.id,
      service_name: connector.serviceName,
      connector_status: connectorStatus,
      is_healthy: isHealthy,
      is_super_admin: isSuperAdmin,
      connection_index: index,
    })
    try {
      await onDisconnect(connector.id)
      void onClose()
    } catch {
      toast.error("Failed to delete connection")
    }
  }

  const handleRemoveMigrationRestrictions = async () => {
    trackRemoveMigrationRestrictions({
      company_connector_id: connector.id,
      service_name: connector.serviceName,
      is_super_admin: isSuperAdmin,
      connection_index: index,
    })
    try {
      const existingMetadata = connector.metadata || {}
      const updatedMetadata = { ...existingMetadata }
      delete updatedMetadata.createdByMigration
      delete updatedMetadata.migrationSource

      await updateCompanyConnector({
        id: connector.id,
        updateCompanyConnectorDto: { metadata: updatedMetadata },
      }).unwrap()

      toast.success(
        "Migration restrictions removed. You can now re-authenticate this connector.",
        { toastId: "migration-restrictions-removed" },
      )
      // updateCompanyConnector already invalidates the CompanyConnectors tag.
    } catch {
      toast.error("Failed to remove migration restrictions", {
        toastId: "migration-restrictions-remove-failed",
      })
    }
  }

  return (
    <div className="border border-gray-600 rounded-lg p-4">
      <b className="text-4 leading-7 font-bold text-platinum mb-3 block">
        {`Connection ${index + 1}`}
      </b>

      <ConnectorStatus
        companyConnectorId={connector.id}
        connectorStatus={connectorStatus}
        hasError={hasError}
      />

      {grantWarning && (
        <div className="mb-4">
          <CredentialGrantWarningChip warning={grantWarning} wrap />
        </div>
      )}

      {hasError && errorData?.message && (
        <div className="mb-4">
          <SetupErrorBanner error={errorData} />
        </div>
      )}

      {isCreatedByMigration && (
        <div className="flex flex-col gap-2 text-left mb-4 p-3 bg-gray-700/50 border border-gray-600 rounded-lg">
          <div className="flex items-center gap-2">
            {renderStatusIcon("warning")}
            <p className="text-4 leading-7 font-bold text-platinum">
              System Managed Connector
            </p>
          </div>
          <p className="text-4 leading-7 font-medium text-gray-300 ml-8">
            This connector was created by the system and cannot be
            re-authenticated through the UI.
          </p>
          {isSuperAdmin && (
            <div className="mt-3 pt-3 border-t border-gray-600">
              <Button
                className="bg-blue-600 hover:bg-blue-700 border border-blue-500 text-white rounded-system-md"
                onClick={handleRemoveMigrationRestrictions}
                disabled={isUpdating}
                isLoading={isUpdating}
              >
                {isUpdating
                  ? "Removing restrictions..."
                  : "Remove Migration Restrictions (Admin)"}
              </Button>
            </div>
          )}
        </div>
      )}

      <AccountInfo
        confirmedAt={connector.confirmedAt}
        companyConnectorId={connector.id}
      />

      {!isLoadingStatus && !isHealthy && (
        <div className="flex justify-start w-full gap-4 h-system-2xl">
          {isSuperAdmin ? (
            <Button
              className="bg-system-blue-700 border border-system-blue-600 text-system-error-400 hover:bg-system-error-400 hover:text-white rounded-system-md"
              onClick={handleDisconnect}
            >
              Disconnect
            </Button>
          ) : null}
          {!isCreatedByMigration &&
            RESETTABLE_STATUSES.includes(
              connectorStatus as ConnectorStatusType,
            ) && (
              <Button
                className="bg-system-blue-700 border border-system-blue-600 text-platinum hover:bg-system-blue-600 rounded-system-md"
                onClick={() => setIsResetDialogOpen(true)}
                title="Delete this connection's setup progress and start over"
              >
                Start over
              </Button>
            )}
          <Button
            className="bg-system-brand-pink-600 border border-system-blue-600 text-white rounded-system-md"
            onClick={handleReconnect}
            disabled={isCreatedByMigration}
            title={
              isCreatedByMigration
                ? "This connector was created by the system migration and cannot be re-authenticated."
                : undefined
            }
          >
            {getButtonText(
              connectorStatus as ConnectorStatusType,
              connector.serviceName,
            )}
          </Button>
        </div>
      )}
      {!isLoadingStatus &&
        isHealthy &&
        (isSuperAdmin || grantNeedsReconnect) && (
          <div className="flex justify-start w-full gap-4 h-system-2xl">
            {isSuperAdmin ? (
              <Button
                className="bg-system-blue-700 border border-system-blue-600 text-system-error-400 hover:bg-system-error-400 hover:text-white rounded-system-md"
                onClick={handleDisconnect}
              >
                Disconnect
              </Button>
            ) : null}
            {grantNeedsReconnect && (
              <Button
                className="bg-system-brand-pink-600 border border-system-blue-600 text-white rounded-system-md"
                onClick={handleReconnect}
                disabled={isCreatedByMigration}
                title={
                  isCreatedByMigration
                    ? "This connector was created by the system migration and cannot be re-authenticated."
                    : undefined
                }
              >
                Reconnect
              </Button>
            )}
          </div>
        )}
      <ResetSetupConfirmDialog
        isOpen={isResetDialogOpen}
        companyConnectorId={connector.id}
        connectorName={`${connectorDefinition?.name ?? connector.serviceName} — Connection ${index + 1}`}
        onClose={() => setIsResetDialogOpen(false)}
        onSuccess={onClose}
      />
    </div>
  )
}

const ConnectionList: React.FC<ConnectionListProps> = ({
  companyConnectorId,
  isSuperAdmin,
  allowMultipleConnections,
  onDisconnect,
  onReconnect,
  onClose,
  onAddConnection,
}) => {
  const { data: connectedServices } =
    useCompanyConnectorControllerGetCompanyConnectorsQuery()

  const currentConnector = connectedServices?.find(
    s => s.id === companyConnectorId,
  )

  const siblingConnectors = useMemo(() => {
    if (!connectedServices || !currentConnector) return []
    return connectedServices.filter(
      s => s.serviceName === currentConnector.serviceName,
    )
  }, [connectedServices, currentConnector])

  // Show list when multiple connections exist, or when a single connection supports adding more
  if (siblingConnectors.length <= 1 && !allowMultipleConnections) {
    return null
  }

  return (
    <article className="w-full mb-4">
      <div className="flex items-center justify-between mb-3">
        <b className="text-4 leading-7 font-bold text-platinum">
          Connections ({siblingConnectors.length})
        </b>
        {allowMultipleConnections && onAddConnection && (
          <Button
            className="bg-system-brand-pink-600 border border-system-blue-600 text-white rounded-system-md text-sm"
            onClick={onAddConnection}
          >
            + Add Connection
          </Button>
        )}
      </div>
      <div className="flex flex-col gap-4">
        {siblingConnectors.map((connector, index) => (
          <ConnectionItemDetails
            key={connector.id}
            connector={connector}
            index={index}
            isSuperAdmin={isSuperAdmin}
            onDisconnect={onDisconnect}
            onReconnect={onReconnect}
            onClose={onClose}
          />
        ))}
      </div>
    </article>
  )
}

export default memo(ConnectionList)
